import { RcmDownloadProp } from '../../components/rcm-generic-link/rcm-generic-link.types';
import { RcmImageProp } from '../../components/rcm-image/rcm-image.types';
import { Multilink, Target } from '../../components/types/link.types';
import { ValidationService } from '../validation';
import { Domain } from '../../../types/domain';
import { DomService } from '../dom';
import { AppService } from '../app';
import { UserType } from '../../../types/user-type';
import { isLocale, Locale } from '../../../types/locale';
import { Country, isCountry } from '../../../types/country';
import { Homepage } from '../../../types/homepage';

const characters = 'abcdefghijklmnopqrstuvwxyz0123456789-_'; // base 64

interface StoryblokResourceMappingEntry {
  sbResource: string;
  buildAs?: string;
  exclude?: string[];
}

interface DomainMappingEntry extends StoryblokResourceMappingEntry {
  domain: Domain;
}

const DEFAULT_HOMEPAGE_REF = 'default';

export const StringService = {
  URL_IN_TEXT_PATTERN:
    /*  eslint-disable-next-line no-useless-escape */
    /(?:(?:https?|ftp|file):\/\/|www\.|ftp\.)(?:\([-A-Z0-9+&@#\/%=~_|$?!:,.]*\)|[-A-Z0-9+&@#\/%=~_|$?!:,.])*(?:\([-A-Z0-9+&@#\/%=~_|$?!:,.]*\)|[A-Z0-9+&@#\/%=~_|$])/gi,

  generate(length = 32): string {
    return [...new Array(length)].map(() => characters[Math.floor(Math.random() * characters.length) % characters.length]).join('');
  },

  camelToKebab(string: string): string {
    return string.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
  },

  capitalize(string: string): string {
    return string.charAt(0).toUpperCase() + string.slice(1);
  },

  kebabToCamel(string: string): string {
    return string.replace(/-([a-z])/g, (_, s) => s.toUpperCase());
  },

  kebabToCamelSpace(string: string): string {
    return string.replace(/-([a-z])/g, (_, s) => ` ${s.toUpperCase()}`);
  },

  kebabToPascalSpace(string: string): string {
    const transformed = StringService.kebabToCamelSpace(string);
    return `${transformed.substr(0, 1).toUpperCase()}${transformed.substr(1)}`;
  },

  stringToObject<T>(input: unknown): T | string {
    if (`${input}`[0] === '{' || `${input}`[0] === '[') {
      try {
        return typeof input === 'string' ? JSON.parse(input) : input;
      } catch (_) {
        // do nothing
      }
    }
    return input as string;
  },

  toTitleCase(sentence: string): string {
    return sentence
      .split(' ')
      .map((word) => [word[0].toUpperCase(), ...word.substr(1)].join(''))
      .join(' ');
  },

  removeTrailingSlash(link) {
    return link.length > 1 && link.endsWith('/') ? link.slice(0, -1) : link;
  },

  isVideoUrl(url: string): boolean {
    const splitUrl = url.split('.');
    const extension = splitUrl[splitUrl.length - 1];
    return ['mp4', 'webm', 'avi'].indexOf(extension.toLowerCase()) >= 0;
  },

  /*
  This method was earlier called isUrl and would return true for both urls and filenames.
  In order to not break any existing behaviour, the functionality and usage are preserved,
  but method is renamed for clarity.
  */
  isUrlOrFilename(link: string): boolean {
    return this.isUrl(link) || this.isFilename(link);
  },

  isUrl(url: string) {
    if (!url || url.includes(' ')) {
      return false;
    }

    const urlRegex = /^(https?:\/\/|www\.)([a-z0-9-]+(\.[a-z0-9-]+)+)(:\d{1,5})?(\/[^\s]*)?(\?[^\s]*)?(#[^\s]*)?$/i;
    return urlRegex.test(url.trim());
  },

  /**
   * Ensures a URL has a proper protocol (https://) if it doesn't already have one
   * @param url The URL to normalize
   * @returns URL with https:// prepended if needed
   */
  ensureUrlProtocol(url: string): string {
    if (!url) return url;

    if (url.startsWith('www.')) {
      return `https://${url}`;
    }

    return url;
  },

  isFilename(filename: string) {
    if (!filename) {
      return false;
    }

    const fileRegex = /\.(jpe?g|png|gif|bmp|webp|svg|avif|apng|pdf|docx?)$/i;
    return fileRegex.test(filename.trim());
  },

  extractCountryLocale(fullSlug?: string): { country: string | null; locale: string | null } | null {
    if (!fullSlug) return null;

    const patterns = [
      /^\/?([a-zA-Z]{2,3})-([a-zA-Z]{2,3})(?:\/|$)/, // Matches "at-de", "at-de/", "/at-de" and "/at-de/..."
      /^\/?([a-zA-Z]{2})\/([a-zA-Z]{2})(?:\/|$)/, // Matches "at/de", "at/de/", "/at/de" and "/at/de/..."
    ];

    const match = patterns.map((pattern) => fullSlug.match(pattern)).find(Boolean);

    if (!match) return null;

    const [, country, locale] = match;

    return isCountry(country) && isLocale(locale) ? { country, locale } : { country: null, locale: null };
  },

  hasCountryLocaleInfo(link?: string): boolean {
    return this.extractCountryLocale(link) !== null;
  },

  createDomainSpecificLink(link: string): string {
    if (!this.hasCountryLocaleInfo(link)) {
      return this.removeTrailingSlash(`/${link}`);
    }

    const { domain: currentDomain } = DomService.getGlobalConfig();
    const bestMatch: DomainMappingEntry | null = this.findBestLinkMatch(link, currentDomain);

    if (!bestMatch) {
      // No match found; ensure leading slash
      return this.removeTrailingSlash(`/${link}`);
    }

    const { domain: matchedDomain, sbResource: matchedResource, buildAs: matchedBuildAs } = bestMatch;
    const transformedLink = this.transformLink(link, matchedResource, matchedBuildAs);

    return this.removeTrailingSlash(this.constructFinalLink(transformedLink, currentDomain, matchedDomain));
  },

  findBestLinkMatch(sbFullSlug: string, currentDomain: Domain): DomainMappingEntry | null {
    const sbFolders = sbFullSlug.split('/');
    const { domainMapping } = DomService.getDomainConfig();

    const matchingDomainMappings: DomainMappingEntry[] = Object.entries(domainMapping)
      .flatMap(
        ([domain, mappings]: [string, StoryblokResourceMappingEntry[]]) =>
          this.mapSbResourcesToDomainMappingEntries(mappings, domain) as DomainMappingEntry[],
      )
      .filter((domainMappingEntry: DomainMappingEntry) => this.isResourceMatch({ sbFolders, domainMappingEntry }))
      .sort((a, b) => b.sbResource.length - a.sbResource.length);

    return matchingDomainMappings.find(({ domain }) => domain === currentDomain) || matchingDomainMappings[0];
  },

  isResourceMatch({ domainMappingEntry, sbFolders }: { domainMappingEntry: DomainMappingEntry; sbFolders: string[] }): boolean {
    const { sbResource, exclude } = domainMappingEntry;

    const entryFolders = sbResource.split('/');

    const isMatchingSbResource = entryFolders.every((entryFolder: string, index: number) => sbFolders[index] === entryFolder);

    // if it is not a matching resource, then it is simply not a match
    if (!isMatchingSbResource) {
      return false;
    }

    // if it is a matching resource, and it does not have any info about excluded folders, then it is a match
    if (!exclude) {
      return true;
    }

    // if it is a matching resource, then we should check if the current resource is still excluded
    // if the current resource is excluded, then it is not a match
    // otherwise it is a match
    return !exclude.find((excludedResource) => {
      const excludedResourceFolders = excludedResource.split('/');

      return excludedResourceFolders.every((excludedEntryFolder: string, index: number) => sbFolders[index] === excludedEntryFolder);
    });
  },

  mapSbResourcesToDomainMappingEntries(sbResourceMappingEntries: StoryblokResourceMappingEntry[], domain: Domain): DomainMappingEntry[] {
    return sbResourceMappingEntries.map(
      ({ sbResource, buildAs, exclude }) =>
        ({
          domain: domain as Domain,
          sbResource,
          buildAs,
          exclude,
        }) as DomainMappingEntry,
    );
  },

  transformLink(link: string, matchedResource: string, buildAs: string): string {
    return link.replace(matchedResource, buildAs).replace(/^\/+/, '');
  },

  constructFinalLink(transformedLink: string, currentDomain: Domain, matchedDomain: Domain): string {
    if (matchedDomain !== currentDomain) {
      const { domainToUrlMapping } = DomService.getDomainConfig();

      // Different domain: prepend full domain URL
      return `${domainToUrlMapping[matchedDomain]}/${transformedLink}`;
    }

    // Same domain: return as relative link with leading slash
    return `/${transformedLink}`;
  },

  normalizeLink(link: string = ''): string {
    if (typeof link !== 'string') {
      return link;
    }
    const leadingSlashWhiteSpacesRegex = /^\/+/;
    const linkWithoutLeadingSlash = link.replace(leadingSlashWhiteSpacesRegex, '').trim();
    const normalizedLink = this.removeTrailingSlash(linkWithoutLeadingSlash);

    if (normalizedLink.startsWith('#') || normalizedLink.startsWith('mailto:') || normalizedLink.startsWith('tel:')) {
      return normalizedLink;
    }

    if (ValidationService.isEmail(normalizedLink)) {
      return `mailto:${normalizedLink}`;
    }

    if (ValidationService.isMobileNumber(normalizedLink)) {
      return `tel:${normalizedLink}`;
    }

    if (this.isUrl(linkWithoutLeadingSlash)) {
      return this.ensureUrlProtocol(linkWithoutLeadingSlash);
    }

    return this.createDomainSpecificLink(normalizedLink);
  },

  getLogoUrlForCurrentDomainAndUserType(): string {
    const currentUserType = AppService.getUserType();
    const { retail, institutional } = DomService.getGlobalContent().logo;

    return currentUserType === UserType.INSTITUTIONAL ? institutional : retail;
  },

  buildHomePageLink(): string {
    const currentUserType = AppService.getUserType();

    const { homepage } = DomService.getGlobalContent().navigation;

    const targetUserTypeRef = currentUserType || DEFAULT_HOMEPAGE_REF;
    const targetHomepageLink = homepage[targetUserTypeRef];
    const finalLink = targetHomepageLink && targetHomepageLink !== '/' ? targetHomepageLink : homepage[DEFAULT_HOMEPAGE_REF] || '';

    return this.removeTrailingSlash(finalLink);
  },

  buildLocalisedHomePageLink({ userType, country, locale }: { userType?: UserType; country?: Country; locale?: Locale }): string {
    const { homepages, country: currentCountry, locale: currentLocale } = DomService.getGlobalConfig();

    const targetCountry = country || currentCountry;
    const targetLocale = locale || currentLocale;

    const targetCountryLocaleCode = homepages[`${targetCountry}-${targetLocale}`]
      ? `${targetCountry}-${targetLocale}`
      : Object.keys(homepages).find(
          (countryLocaleCode) => countryLocaleCode === `${targetCountry}-${targetLocale}` || countryLocaleCode.startsWith(targetCountry),
        );

    const targetHomepage: Homepage = homepages[targetCountryLocaleCode] || DomService.getGlobalContent().navigation.homepage;

    const { domain: currentDomain } = DomService.getGlobalConfig();
    const fallBackUserType = currentDomain === Domain.INSTI ? UserType.INSTITUTIONAL : UserType.RETAIL;
    const targetUserType = userType || AppService.getUserType() || fallBackUserType;
    const finalLink = targetHomepage[targetUserType] || targetHomepage[DEFAULT_HOMEPAGE_REF] || '';

    return this.removeTrailingSlash(finalLink);
  },

  getUserTypeFromSlug(fullSlug: string, localizedUserTypeSlug: { retail: string; institutional: string }): UserType | null {
    const { retail: retailSlug, institutional: institutionalSlug } = localizedUserTypeSlug;

    // Break the fullSlug into parts by slashes
    const slugParts = fullSlug.split('/');

    // Check if any part matches the retail slug
    if (slugParts.includes(retailSlug)) {
      return UserType.RETAIL;
    }

    // Check if any part matches the institutional slug
    if (slugParts.includes(institutionalSlug)) {
      return UserType.INSTITUTIONAL;
    }

    // If no match, return null
    return null;
  },

  parseLink(link, target?: Target): Multilink {
    const parsedLink: Multilink | RcmDownloadProp | string = typeof link === 'string' ? StringService.stringToObject(link) : link;
    if (typeof parsedLink !== 'string' && parsedLink?.src) {
      return typeof target !== 'undefined' ? { ...parsedLink, target } : parsedLink;
    }
    return { src: parsedLink as string, target: target ?? Target.SELF };
  },

  isValidImageSrc(str: string | RcmImageProp): boolean {
    if (!str) {
      return false;
    }

    const imageSrc = typeof str === 'string' ? str : str?.src;
    if (!imageSrc) {
      return false;
    }

    if (this.isUrl(imageSrc)) {
      return true;
    }

    const imageRegex = /\.(jpe?g|png|gif|bmp|webp|svg|avif|apng)$/i;
    return imageRegex.test(imageSrc);
  },

  getLanguageFromSlug(fullSlug: string): string {
    return fullSlug.match(/-(\w+)\//)?.[1];
  },

  // Utility function to generate Storyblok Image Service URL
  getStoryblokImageURL(src: string, width: number): string {
    return `${src}/m/${width}x0`;
  },
};
